// components/Hero.jsx
import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Stack,
  HStack,
  VStack,
  Badge,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react';

import { motion } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import AssessmentFlow from "./assessment/AssessmentFlow";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);

const Hero = ({ data = {}, onEnrollClick }) => {
  const { trackEvent } = useAnalytics();

  const {
    badge,
    title,
    description,
    ctaPrimary,
    ctaSecondary,
    trustElements = [],
    stats = []
  } = data;

  return (
    <Box
      position="relative"
      minH="80vh"
      bgGradient="linear(to-br, brand.700, brand.900, accent.green)"
      overflow="hidden"
    >
      <Container maxW="container.xl" pt={{ base: 16, md: 24 }} pb={20}>
        <Stack spacing={8} >

          {/* Badge */}
          {badge && (
            <MotionBox
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge
                px={4}
                py={2}
                borderRadius="full"
                bg="accent.gold"
                backdropFilter="blur(10px)"
                color="brand.800"
                fontSize="sm"
                fontWeight="600"
              >
                {badge}
              </Badge>
            </MotionBox>
          )}

          {/* Title */}
          <MotionHeading
            as="h1"
            fontSize={{ base: '4xl', md: '6xl', lg: '7xl' }}
            fontWeight="900"
            color="white"
            lineHeight="1.1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {title}
          </MotionHeading>

          {/* Description */}
          <MotionText
            fontSize={{ base: 'lg', md: '2xl' }}
            color="whiteAlpha.900"
            maxW="3xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {description}
          </MotionText>

          {/* CTA Buttons */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              {ctaPrimary && <AssessmentFlow />
              }

              {/* ctaSecondary && (
                <Button
                  as={RouterLink}
                  to={ctaSecondary.link}
                  size="lg"
                  h="60px"
                  px={8}
                  fontSize="lg"
                  borderColor="white"
                  variant="outline"
                  color="white"
                >
                  {ctaSecondary.label}
                </Button>
              )
              */  }
            </Stack>
          </MotionBox>

          {/* Trust icons */}
          {!!trustElements?.length && (
            <MotionBox
              pt={8}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <HStack spacing={8} flexWrap="wrap" justify="center">
                {trustElements.map((item, i) => (
                  <HStack key={i} color="white">
                    <Icon as={item.icon} boxSize={5} color="accent.gold" />
                    <Text fontWeight="600" fontSize="sm">
                      {item.text}
                    </Text>
                  </HStack>
                ))}
              </HStack>
            </MotionBox>
          )}

          {/* Stats */}
          {!!stats?.length && (
            <MotionBox
              pt={12}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <SimpleGrid
                columns={{ base: 2, md: stats.length }}
                spacing={8}
              >
                {stats.map((item, i) => (
                  <VStack key={i} color="white">
                    <Icon as={item.icon} boxSize={8} color="accent.gold" />
                    <Text fontWeight="700" fontSize="2xl">
                      {item.value}
                    </Text>
                    <Text fontSize="sm" opacity={0.8}>
                      {item.label}
                    </Text>
                  </VStack>
                ))}
              </SimpleGrid>
            </MotionBox>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default Hero;
